import { DataConverter } from "Global/Base/Converters/DataConverter";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { BasicDataType } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { IBeforeSaveAwaitable } from "UI/UI.BaseControls/ControlInterfaces";
import { IFileUploadStatusChangeEvent } from "UI/UI.Controls/IEvent";
import { ControlParams, IRequiresActualObject } from "UI/UI/UI/Controls/ControlInterfaces";
import { FileEdit } from "UI/UI/UI/Controls/FileEdit";
import { FileEditStatus } from "UI/UI/UI/FileMenu";
import { UploadManager } from "UI/UI/UI/UploadManager";
import { PdfEditElement } from "./PdfEditElement";

const DEFAULT_PREVIEW_SIZE = 52_428_800; // Výchozí velikost 50MiB

export class PdfEdit extends FileEdit implements IRequiresActualObject, IBeforeSaveAwaitable {
    private _pdfEdit: PdfEditElement;
    private _inEditMode: boolean;

    private _objectImageAttributeChangeListener: (sender: OWObject, prm: string) => void;
    private _imageUploaderListeners: CaseInsensitiveMap<() => void> = new CaseInsensitiveMap();
    private _uploadFinishedListener: () => void;
    private _uploadStartedListener: () => void;

    private _beforeSaveExecutable: boolean = false;

    /** Velikost limitu pro zobrazení náhledu v bytech */
    protected _previewFileSizeLimit: number | undefined = undefined;

    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public override set actualObject(value: OWObject) {
        if (this._actualObject != null) {
            this._pdfEdit.src = undefined;

            const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
            if (uploaderData && uploaderData.uploader) {
                uploaderData.uploader.onStart.remove(this._imageUploaderListeners.get("start"));
                uploaderData.uploader.onFinished.remove(this._imageUploaderListeners.get("finished"));
            }

            if (this._objectImageAttributeChangeListener) {
                this._actualObject.attributeChanged.remove(this._objectImageAttributeChangeListener);
                this._objectImageAttributeChangeListener = null;
            }
        }

        if (!this._actualObject || !value) {
            this._pdfEdit.setStatus(FileEditStatus.unavailable);
        }

        this._pdfEdit.hasObject = value != null;
        super.actualObject = value;

        if (this._actualObject != null) {
            this._actualObject.attributeChanged.add(this._objectImageAttributeChangeListener =
                (sender, attr) => {
                    this._onAttributeChanged(sender, attr);
                });
            this._updatePreview();
        }
    }

    constructor(params: PdfEditControlParams) {
        super(params);
        this.element.classList.add("pdf-viewer-edit");
        this.element.setAttribute("data-behaviour", "component");
        const h = parseInt(this.element.getAttribute("data-height"));
        if (!isNaN(h) && h > 0) {
            this.element.style.minHeight = `${h}px`;
        }

        if (this._pdfEdit) this._pdfEdit.annotationUserName = params?.userName;

        this._previewFileSizeLimit = params?.previewFileSizeLimit ?? DEFAULT_PREVIEW_SIZE;

        this.enabledChanged.add((_s, v) => {
            this._pdfEdit.enabled = v?.value;
            if (v?.value && this._inEditMode && !this._pdfEdit.inEditMode) {
                this._pdfEdit.inEditMode = this._inEditMode;
            }
            if (this._pdfEdit.enabled && !this._pdfEdit.src && this._actualObject)
                this._pdfEdit.setStatus(FileEditStatus.empty);
        });

        this.isEditingChanged.add((_s, v) => {
            this._inEditMode = v.value;
            this._pdfEdit.inEditMode = v.value && this.enabled;
        });

        this._uploadFinishedListener = async () => {
            this._updatePreview();
        };

        this._uploadStartedListener = () => {
            this._pdfEdit.src = undefined;
            this._pdfEdit.setStatus(FileEditStatus.uploading);
        };
    }

    private async _updatePreview(): Promise<void> {
        if (this._actualObject) {
            const value = await this._actualObject.getValue(this.attrName);

            if (value) {
                try {
                    await this.waitForPendingActions();

                    if (!(this._fileName?.toLocaleUpperCase().endsWith(".PDF") ?? true)) {
                        this._pdfEdit.src = undefined;
                        this._pdfEdit.setStatus(FileEditStatus.previewNotSupported);
                        return;
                    }
                    if (this._previewFileSizeLimit) {
                        const metadata = await this._attributeData.getMetadata();
                        if (metadata.FileSize) {
                            const fileSize = parseInt(metadata.FileSize);
                            if (fileSize > this._previewFileSizeLimit) {
                                this._pdfEdit.src = undefined;
                                this._pdfEdit.setStatus(FileEditStatus.fileSizeExceed);
                                return;
                            }
                        }
                    }
                    const attributeMetadata = await this._actualObject.getAttributeMetadata(this.attrName);

                    if (attributeMetadata.basicType == BasicDataType.Binary) {
                        this._pdfEdit.src = "data:application/pdf;base64," + DataConverter.bufferToBase64(value.valueOf());
                    } else {
                        this._pdfEdit.src = await OWManager.getDocumentDownloadUrl(this._actualObject.instanceID, this.attrName, undefined, this._getRequestContext());
                    }
                } catch (e) {
                    this._pdfEdit.src = undefined;
                    this._pdfEdit.setStatus(FileEditStatus.previewNotSupported); //\e0a3
                }
            } else {
                this._pdfEdit.src = undefined;
            }
        } else {
            this._pdfEdit.src = undefined;
        }
    }

    private _onAttributeChanged(_sender: OWObject, changedAttribute: string): void {
        if (changedAttribute != this.attrName)
            return;

        this._actualObject.getAttributeMetadata(this.attrName).then(attributeMetadata => {
            if (attributeMetadata.basicType == BasicDataType.Binary) {
                this._onBinaryAttributeChanged();
            } else {
                this._onDocumentAttributeChanged();
            }
        });
    }

    private _onBinaryAttributeChanged(): void {
        this._updatePreview();
    }

    private _onDocumentAttributeChanged(): void {
        //if (this._lastSetuploaderIID == this._actualObject.instanceID) return;
        //this._lastSetuploaderIID = this._actualObject.instanceID;

        const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
        const uploader = uploaderData.uploader;

        this._imageUploaderListeners.set("start", this._uploadStartedListener);

        if (!uploader.onStart.containsListener(this._uploadStartedListener)) {
            uploader.onStart.add(this._uploadStartedListener);
        }

        if (!uploader.onFinished.containsListener(this._uploadFinishedListener)) {
            uploader.onFinished.add(this._uploadFinishedListener);
        }

        this._imageUploaderListeners.set("finished", this._uploadFinishedListener);

        if (uploader.isComplete)
            this._updatePreview();
        if (!uploader.isStarted && !uploader.isActive && !uploader.isComplete && !uploader.isError) this._updatePreview();
    }

    public beforeSave(): Promise<void> {
        if (this._beforeSaveExecutable) {
            this._beforeSaveExecutable = false;
        } else return Promise.resolve();

        return new Promise((resolve) => {
            const eventFunction = (e: IFileUploadStatusChangeEvent): void => {
                if (e.detail.status != FileEditStatus.uploading) {
                    this._fileEditElement.unregisterEventListener("statusChange", eventFunction);
                    resolve();
                }
            };

            this._fileEditElement.registerEventListener("statusChange", eventFunction);

            this._pdfEdit.getDocument().then((r) => {
                this._fileEditElement.callEvent("uploadRequest", {
                    files: [ r ],
                });
            });
        });
    }

    protected override _createFileElement(): void {
        const features = this.element.getAttribute("data-Features");
        this._pdfEdit = new PdfEditElement();
        this._fileEditElement = this._pdfEdit.fileEdit;
        this._pdfEdit.enabled = this.enabled;
        this._pdfEdit.features = features ? features.split(",").map(s => s.trim()) : [];

        //this.clicked.trigger(this, e);

        this._pdfEdit.documentUpdate.add(async () => {
            this._beforeSaveExecutable = true;
        });

        this.element.appendChild(this._pdfEdit as HTMLElement);
    }

    protected override _setListeners(): void {
        super._setListeners();
    }

    public override dispose(): void {
        if (this._actualObject) {
            const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
            if (uploaderData && uploaderData.uploader) {
                uploaderData.uploader.onStart.remove(this._imageUploaderListeners.get("start"));
                uploaderData.uploader.onStart.remove(this._uploadStartedListener);
                this._uploadStartedListener = null;

                uploaderData.uploader.onFinished.remove(this._imageUploaderListeners.get("finished"));
                uploaderData.uploader.onFinished.remove(this._uploadFinishedListener);
                this._uploadFinishedListener = null;
            }

            if (this._objectImageAttributeChangeListener) {
                this._actualObject.attributeChanged.remove(this._objectImageAttributeChangeListener);
                this._objectImageAttributeChangeListener = null;
            }
        }

        this._actualObject = null;

        super.dispose();
    }
}

export type PdfEditControlParams = ControlParams & {
    previewFileSizeLimit?: number;
    userName?: string;
};
